import { useCallback, useEffect, useMemo, useRef } from 'react';

import {
  useAddLayout,
  useDeleteLayoutById,
  useGetAllLayoutsByUser,
  useUpdateLayout,
} from '@restDataAccess';
import { useSnackbar } from '@scaleaq/scaleui';
import { AddLayoutRequest, LayoutUpdateRequest, SiteControlLayout } from '@scaleaq/sitecontrol-lib';
import { useTranslation } from 'react-i18next';

import { PageNames, StaticPageNames } from '@/constants';
import { useConfigStore, useLayoutStorage } from '@/stores';
import { createSnackbarErrorMessage, createSnackbarSuccessMessage } from '@/utils';

/**
 * Layout Database Sync Hook
 *
 * Data Flow Architecture:
 * 1. Database (DB) is the source of truth - all layouts are persisted here
 * 2. Local Storage (Zustand) is a cache/optimistic update layer for:
 *    - Immediate UI updates before DB operations complete
 *    - Preserving newly created layouts until DB refetch completes
 *    - Offline/performance optimization
 * 3. Sync Function (`setLocalStorageFromDatabaseLayouts`) reconciles differences:
 *    - Merges DB layouts with storage layouts
 *    - Preserves newly created layouts not yet in DB
 *    - Ensures currentLayoutId is correctly set
 *
 * State Update Patterns:
 * - Direct storage updates: Use for immediate UI feedback (optimistic updates)
 * - Sync function: Use to reconcile DB and storage after mutations/refetches
 * - getState() access: Used in callbacks/effects where hook selectors would cause stale closures
 */

type LayoutDBSyncReturn = (pageFilter?: PageNames) => {
  updateLayoutInDb: (updateRequest: LayoutUpdateRequest) => Promise<SiteControlLayout>;
  addLayoutToDb: (addLayoutRequest: AddLayoutRequest | undefined) => Promise<SiteControlLayout>;
  addOrUpdateLoader: boolean;
  allDbLayouts: SiteControlLayout[] | undefined;
  deleteLoader: boolean;
  fetchLoader: boolean;
  getLayoutById: (id?: string) => SiteControlLayout | undefined;
  getStaticDbLayoutByPageName: (staticName: StaticPageNames) => SiteControlLayout | undefined;
  removeLayout: (layout?: SiteControlLayout) => void;
  source: string;
  setLocalStorageFromDatabaseLayouts: () => void;
};

export const useLayoutDBSync: LayoutDBSyncReturn = pageFilter => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation(['common', 'landingPage']);
  const { clusterName } = useConfigStore();

  const removeLayoutFromStorage = useLayoutStorage(s => s.removeLayoutFromStorage);
  const setCurrentLayoutIdInStorage = useLayoutStorage(s => s.setCurrentLayoutIdInStorage);
  const setStorageLayouts = useLayoutStorage(s => s.setStorageLayouts);
  const getCurrentLayoutFromStorage = useLayoutStorage(s => s.getCurrentLayoutFromStorage);

  const { data, isLoading: fetchLoader } = useGetAllLayoutsByUser();
  const allDbLayouts = useMemo(() => {
    if (!data || !data.length) return undefined;
    return data;
  }, [data]);
  const { mutate: deleteLayout, isPending: deleteLoader } = useDeleteLayoutById();

  const { mutateAsync: addLayoutAsync, isPending: addLoader } = useAddLayout();

  const { mutateAsync: updateLayout, isPending: updatingLoader } = useUpdateLayout();

  const getLayoutById = useCallback(
    (id?: string) => {
      if (!id) {
        return undefined;
      }
      return allDbLayouts?.find(layout => layout.id === id);
    },
    [allDbLayouts]
  );

  const getStaticDbLayoutByPageName = useCallback(
    (staticName: StaticPageNames) => {
      return allDbLayouts?.find(layout => layout.pageName === staticName);
    },
    [allDbLayouts]
  );

  const removeLayout = useCallback(
    (layout?: SiteControlLayout) => {
      if (!layout) {
        enqueueSnackbar(t('landingPage:layoutUndefined'), {
          variant: 'alarm',
          autoHideDuration: 3000,
          key: `layout-not-deleted`,
        });
        return;
      }

      deleteLayout(layout, {
        onSuccess: () => {
          createSnackbarSuccessMessage('deleted', enqueueSnackbar, layout.name, t)();
          try {
            removeLayoutFromStorage(layout.id);
          } catch (error) {
            console.error('Failed to remove layout from storage after deleting:', error);
          } finally {
            // Using getState() to read fresh state in callback (hook selectors would cause stale closures)
            const storageState = useLayoutStorage.getState();
            const currentLayoutId = storageState.currentLayoutId;

            // If the deleted layout was current, set to empty string (redirect logic will handle navigation)
            if (currentLayoutId === layout.id) {
              setCurrentLayoutIdInStorage('');
            }
          }
        },
        onError: createSnackbarErrorMessage('delete', enqueueSnackbar, layout.name, t),
      });
    },
    [deleteLayout, enqueueSnackbar, removeLayoutFromStorage, setCurrentLayoutIdInStorage, t]
  );

  const addLayoutToDb = useCallback(
    async (addLayoutRequest: AddLayoutRequest | undefined) => {
      if (!addLayoutRequest) {
        const err = new Error('AddLayoutRequest is undefined');
        enqueueSnackbar(t('landingPage:layoutUndefined'), {
          variant: 'alarm',
          autoHideDuration: 3000,
          key: 'layout-not-added',
        });
        throw err; // rejected Promise<SiteControlLayout>
      }
      try {
        const response: SiteControlLayout = await addLayoutAsync(addLayoutRequest);
        createSnackbarSuccessMessage('added', enqueueSnackbar, addLayoutRequest.name, t)();
        return response;
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        createSnackbarErrorMessage('add', enqueueSnackbar, addLayoutRequest.name, t)({ message });
        throw err;
      }
    },
    [addLayoutAsync, enqueueSnackbar, t]
  );

  const updateLayoutInDb = useCallback(
    async (updateRequest: LayoutUpdateRequest) => {
      return updateLayout(updateRequest, {
        onSuccess: response => {
          createSnackbarSuccessMessage('updated', enqueueSnackbar, updateRequest.name || '', t)();
          return response;
        },
        onError: err => {
          const message = err instanceof Error ? err.message : String(err);
          createSnackbarErrorMessage(
            'update',
            enqueueSnackbar,
            updateRequest.name || '',
            t
          )({ message });
          return err;
        },
      });
    },
    [enqueueSnackbar, t, updateLayout]
  );

  /**
   * Syncs local storage with database layouts.
   * Merges DB layouts with storage layouts to preserve newly created layouts until refetch completes.
   */
  const setLocalStorageFromDatabaseLayouts = useCallback(() => {
    if (fetchLoader) return;

    try {
      const currentLocalLayout = getCurrentLayoutFromStorage();
      const currentId = currentLocalLayout?.id;
      // Using getState() to read fresh state in callback (hook selectors would cause stale closures)
      const storageLayoutsSnapshot = useLayoutStorage.getState().storageLayouts;

      if (allDbLayouts && allDbLayouts.length > 0) {
        // Merge DB layouts with storage layouts not yet in DB (preserves newly created layouts)
        const storageLayoutsNotInDb = storageLayoutsSnapshot.filter(
          storageLayout => !allDbLayouts.some(dbLayout => dbLayout.id === storageLayout.id)
        );
        const mergedLayouts = [...allDbLayouts, ...storageLayoutsNotInDb];
        setStorageLayouts(mergedLayouts);

        // Only preserve current layout ID if it exists in merged layouts (no automatic selection)
        const currentIdExists = mergedLayouts.some(l => l.id === currentId);
        if (currentIdExists && currentId) {
          setCurrentLayoutIdInStorage(currentId);
        }
        return;
      }

      // No DB layouts → only preserve existing currentId if it exists in storage (no automatic selection)
      if (currentId && storageLayoutsSnapshot.some(l => l.id === currentId)) {
        return;
      }
    } catch (error) {
      console.error('Failed to sync storage with database layouts:', error);
      // Don't throw - allow app to continue with current storage state
    }
  }, [
    allDbLayouts,
    fetchLoader,
    getCurrentLayoutFromStorage,
    setCurrentLayoutIdInStorage,
    setStorageLayouts,
  ]);

  // Sync on initial load only (prevents overwriting newly created layouts on refetch)
  const didInitialSyncRef = useRef(false);
  useEffect(() => {
    if (!fetchLoader && !didInitialSyncRef.current) {
      didInitialSyncRef.current = true;
      setLocalStorageFromDatabaseLayouts();
    }
  }, [fetchLoader, setLocalStorageFromDatabaseLayouts]);

  return useMemo(
    () => ({
      setLocalStorageFromDatabaseLayouts,
      addLayoutToDb,
      updateLayoutInDb,
      addOrUpdateLoader: addLoader || updatingLoader,
      allDbLayouts: pageFilter
        ? allDbLayouts?.filter(l => l.pageName === pageFilter)
        : allDbLayouts,
      deleteLoader,
      fetchLoader,
      getLayoutById,
      getStaticDbLayoutByPageName,
      removeLayout,
      source: clusterName || 'unknown_cluster_name',
    }),
    [
      addLayoutToDb,
      addLoader,
      allDbLayouts,
      clusterName,
      deleteLoader,
      fetchLoader,
      getLayoutById,
      getStaticDbLayoutByPageName,
      pageFilter,
      removeLayout,
      setLocalStorageFromDatabaseLayouts,
      updateLayoutInDb,
      updatingLoader,
    ]
  );
};
